/*

  Playername.ts

  Player name component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IGame, IView } from "./types";
import { ComponentFunc, m } from "./mithril";
import { glyph, nbsp } from "./util";

interface IAttributes {
  view: IView;
  side: string;
}

export const PlayerName: ComponentFunc<IAttributes> = (initialVnode) => {
  // Displays a player name, handling both human and robot players
  // as well as left and right side, and local and remote colors
  const { view } = initialVnode.attrs;
  const model = view.model;

  function lookAtPlayer(game: IGame, ev: Event, player: number | null, side: number) {
    if (!(model.state?.uiFullscreen))
      // Don't do anything on mobile, and allow the click
      // to propagate to the parent
      return;
    if (player === side) {
      // The player is clicking on himself:
      // overlay a user preference dialog
      view.pushDialog("userprefs");
    }
    else if (!game.autoplayer[side]) {
      // Clicking on another human player: show their track record
      view.showUserInfo(
        game.userid[side],
        game.nickname[side],
        game.fullname[side]
      );
    }
    ev.stopPropagation();
    ev.preventDefault();
  }

  return {
    view: (vnode) => {
      const game = model.game;
      if (!game) return undefined;
      const apl0 = game.autoplayer[0];
      const apl1 = game.autoplayer[1];
      const nick0 = game.nickname[0];
      const nick1 = game.nickname[1];
      const player = game.player;
      const localturn = game.localturn;
      const gameover = game.over;
      const side = vnode.attrs.side;
      if (side == "left") {
        // Left side player
        if (apl0)
          // Player 0 is a robot (autoplayer)
          return m(".robot-btn.left", [glyph("cog"), nbsp(), nick0]);
        const tomove = gameover || (localturn !== (player === 0)) ? "" : ".tomove";
        return m(
          ".player-btn.left" + tomove,
          { id: "player-0", onclick: (ev: Event) => lookAtPlayer(game, ev, player, 0) },
          [m("span.left-to-move"), nick0]
        );
      }
      else {
        // Right side player
        if (apl1)
          // Player 1 is a robot (autoplayer)
          return m(".robot-btn.right", [glyph("cog"), nbsp(), nick1]);
        const tomove = gameover || (localturn !== (player === 1)) ? "" : ".tomove";
        return m(
          ".player-btn.right" + tomove,
          { id: "player-1", onclick: (ev: Event) => lookAtPlayer(game, ev, player, 1) },
          [m("span.right-to-move"), nick1]
        );
      }
    }
  };
};
